import { useEffect, useMemo, useState } from 'react';
import { DateInputDDMMYYYY } from '../../../components/shared/DateInputDDMMYYYY';
import { FilterSelect } from '../../../components/shared/FilterSelect';
import {
  engagementMetricsApi,
  type DashboardQuery,
  type EngagementMetricsDashboardDto,
  type LogAttendanceResponseDto,
} from '../../../api/engagementMetrics';
import { useUserAssignedBrgs } from '../../../hooks/useUserAssignedBrgs';
import { CompanyFilterScopeNote } from './CompanyFilterScopeNote';
import { MembershipTrendsCard } from './MembershipTrendsCard';
import { EngagementMetricsChart } from './EngagementMetricsChart';
import { LogAttendanceModal } from './LogAttendanceModal';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfThisYearIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

function shiftedYearIso(iso: string, deltaYears: number): string {
  const [yStr, m, d] = iso.split('-');
  const y = Number(yStr) + deltaYears;
  return `${y}-${m}-${d}`;
}

function defaultQuery(): DashboardQuery {
  const p1Start = firstOfThisYearIso();
  const p1End = todayIso();
  return {
    brgId: 'all',
    company: '',
    p1Start,
    p1End,
    p2Start: shiftedYearIso(p1Start, -1),
    p2End: shiftedYearIso(p1End, -1),
  };
}

export default function EngagementMetricsPage() {
  const { data: brgs = [] } = useUserAssignedBrgs();
  const [query, setQuery] = useState<DashboardQuery>(defaultQuery());
  const [data, setData] = useState<EngagementMetricsDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [banner, setBanner] = useState<{ text: string; key: number } | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [companies, setCompanies] = useState<string[]>([]);

  // Fetch companies from API
  useEffect(() => {
    engagementMetricsApi.companies().then(resp => {
      setCompanies(resp.companies);
    }).catch(() => {
      // Silently fail — dropdown will just show "All Companies"
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    engagementMetricsApi
      .dashboard(query)
      .then(resp => {
        if (!cancelled) setData(resp);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, refreshTick]);

  // Auto-dismiss banner after 5s (AC14d)
  useEffect(() => {
    if (!banner) return;
    const handle = window.setTimeout(() => setBanner(null), 5000);
    return () => window.clearTimeout(handle);
  }, [banner]);

  const period1Year = useMemo(() => Number(query.p1End.split('-')[0]) || new Date().getFullYear(), [query.p1End]);
  const period2Year = useMemo(() => Number(query.p2End.split('-')[0]) || new Date().getFullYear() - 1, [query.p2End]);

  function updateQuery(patch: Partial<DashboardQuery>) {
    setQuery(prev => ({ ...prev, ...patch }));
  }

  function clearFilters() {
    setQuery(defaultQuery());
  }

  function onAttendanceSaved(resp: LogAttendanceResponseDto) {
    setModalOpen(false);
    setBanner({ text: resp.banner.text, key: Date.now() });
    setRefreshTick(t => t + 1);
  }

  const totals = data?.totals ?? { totalEventsPeriod1: 0, totalEventsPeriod2: 0 };
  const trends = data?.membershipTrends ?? [];
  const eventRows = data?.eventAttendance ?? [];
  const isEmpty = totals.totalEventsPeriod1 === 0 && totals.totalEventsPeriod2 === 0;

  return (
    <div data-testid="engagement-metrics-page" style={{ width: '100%', maxWidth: 'none', padding: '16px 80px' }}>
      {/* Filter bar */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '8px 24px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center', rowGap: '8px' }}>
          <FilterSelect
            label="BRG:"
            data-testid="filter-brg"
            value={query.brgId ?? 'all'}
            onChange={e => updateQuery({ brgId: (e.target as HTMLSelectElement).value })}
          >
            <option value="all">All BRGs</option>
            {brgs.map((brg: any) => (
              <option key={brg.brgId} value={brg.brgId}>{brg.name}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Company:"
            data-testid="filter-company"
            value={query.company ?? ''}
            onChange={e => updateQuery({ company: (e.target as HTMLSelectElement).value })}
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </FilterSelect>

          <div style={{ width: '100%', minHeight: '0' }}></div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', minWidth: 'fit-content' }}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{ background: '#2563eb', color: 'white', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                Period 1
              </div>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', minWidth: '42px' }}>Begin:</label>
              <DateInputDDMMYYYY
                value={query.p1Start}
                onChange={(val) => updateQuery({ p1Start: val })}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', width: '130px' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', minWidth: '32px' }}>End:</label>
              <DateInputDDMMYYYY
                value={query.p1End}
                onChange={(val) => updateQuery({ p1End: val })}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', width: '130px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                Period 2
              </div>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', minWidth: '42px' }}>Begin:</label>
              <DateInputDDMMYYYY
                value={query.p2Start}
                onChange={(val) => updateQuery({ p2Start: val })}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', width: '130px' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', minWidth: '32px' }}>End:</label>
              <DateInputDDMMYYYY
                value={query.p2End}
                onChange={(val) => updateQuery({ p2End: val })}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', width: '130px' }}
              />
            </div>
          </div>

          {(query.brgId !== 'all' || query.company) && (
            <button 
              onClick={() => clearFilters()}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #d1d5db',
                padding: '6px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap'
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <CompanyFilterScopeNote
        active={data?.companyFilterScope.active ?? false}
        text={data?.companyFilterScope.infoNote ?? null}
      />

      {/* Banner (AC14d) */}
      {banner && (
        <div
          key={banner.key}
          role="status"
          aria-live="polite"
          data-testid="attendance-banner"
          style={{
            background: '#d4edda',
            color: '#1a7c3a',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '12px',
            fontSize: '13px',
          }}
        >
          {banner.text}
        </div>
      )}

      {/* Body */}
      <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: '12px' }}>
        {/* Left: stats panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <StatsCard
            label={`Total Events (Period 1)`}
            value={totals.totalEventsPeriod1}
            bg="#1a3bcc"
            color="#fff"
            testId="stats-p1"
          />
          <StatsCard
            label={`Total Events (Period 2)`}
            value={totals.totalEventsPeriod2}
            bg="#e8eaf6"
            color="#1a237e"
            testId="stats-p2"
          />
          <MembershipTrendsCard trends={trends} />
        </div>

        {/* Right: chart area */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong style={{ fontSize: '14px' }}>Event Attendance by BRG</strong>
            <button
              data-testid="open-log-attendance"
              onClick={() => setModalOpen(true)}
              style={{
                background: '#1a3bcc',
                color: '#fff',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              + Log Attendance
            </button>
          </div>

          {loading && (
            <div data-testid="loading-spinner" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading…
            </div>
          )}

          {!loading && error && (
            <div data-testid="dashboard-error" role="alert" style={{ color: '#c62828', padding: '12px' }}>
              {error}
            </div>
          )}

          {!loading && !error && isEmpty && (
            <div data-testid="empty-state" style={{ background: '#f9fafb', padding: '60px 40px', textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
              <div style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}>&#x1f50d;</div>
              <div style={{ fontWeight: '600', fontSize: '16px', color: '#1f2937', marginBottom: '8px' }}>No events found</div>
              <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>No events match your selected filters. Try adjusting your criteria.</div>
              <button
                data-testid="clear-filters"
                onClick={clearFilters}
                style={{
                  background: 'transparent',
                  color: '#2563eb',
                  border: '1px solid #d1d5db',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Clear filters
              </button>
            </div>
          )}

          {!loading && !error && !isEmpty && (
            <EngagementMetricsChart rows={eventRows} period1Year={period1Year} period2Year={period2Year} />
          )}
        </div>
      </div>

      {/* BRG Color Key */}
      {brgs.length > 0 && (
        <div style={{ marginTop: '24px', padding: '16px', background: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>BRG Color Key</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {brgs.map((brg: any) => (
              <div key={brg.brgId} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    background: brg.colorCode || '#999999',
                    border: '1px solid #d1d5db',
                  }}
                />
                <span style={{ fontSize: '12px', color: '#374151' }}>{brg.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <LogAttendanceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={onAttendanceSaved}
      />
    </div>
  );
}

function StatsCard({
  label,
  value,
  bg,
  color,
  testId,
}: {
  label: string;
  value: number;
  bg: string;
  color: string;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: bg,
        color,
        padding: '12px',
        borderRadius: '6px',
        textAlign: 'left',
      }}
    >
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginTop: '4px' }}>{value}</div>
    </div>
  );
}

// PeriodBadge component - declared but not used, commented out
// function _PeriodBadge({
//   label,
//   badgeColor,
//   badgeText,
//   start,
//   end,
//   onChange,
//   startTestId,
//   endTestId,
// }: {
//   label: string;
//   badgeColor: string;
//   badgeText: string;
//   start: string;
//   end: string;
//   onChange: (start: string, end: string) => void;
//   startTestId: string;
//   endTestId: string;
// }) {
//   return (
//     <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#333' }}>
//       <span
//         style={{
//           background: badgeColor,
//           color: badgeText,
//           padding: '3px 8px',
//           borderRadius: '12px',
//           fontWeight: 600,
//         }}
//       >
//         {label}
//       </span>
//       <input
//         type="date"
//         data-testid={startTestId}
//         value={start}
//         onChange={e => onChange(e.target.value, end)}
//         style={{ padding: '3px', fontSize: '12px' }}
//       />
//       <span>—</span>
//       <input
//         type="date"
//         data-testid={endTestId}
//         value={end}
//         onChange={e => onChange(start, e.target.value)}
//         style={{ padding: '3px', fontSize: '12px' }}
//       />
//     </div>
//   );
// }
